import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import { useInactivityTimeout } from '../hooks/useInactivityTimeout';

export type TipoUsuario = 'Cliente' | 'Arquiteto' | 'Assistente' | 'Master';

export interface AuthUser {
  id: string;
  nome: string;
  email: string;
  tipo: TipoUsuario;
  temSenhaLocal?: boolean;
  requerTrocaSenha?: boolean;
}

interface AuthContextData {
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (token: string, user: AuthUser) => void;
  logout: () => void;
  updateUser: (updates: Partial<AuthUser>) => void;
}

const AuthContext = createContext<AuthContextData | undefined>(undefined);

const STORAGE_TOKEN_KEY = 'auth_token';
const STORAGE_USER_KEY = 'auth_user';
const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for token in URL params (cross-domain redirect from main site)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('token');
    const urlUser = params.get('user');
    if (urlToken && urlUser) {
      try {
        const userData = JSON.parse(urlUser);
        localStorage.setItem(STORAGE_TOKEN_KEY, urlToken);
        localStorage.setItem(STORAGE_USER_KEY, urlUser);
        setToken(urlToken);
        setUser(userData);
        // Clean URL
        window.history.replaceState({}, '', window.location.pathname);
        setIsLoading(false);
        return;
      } catch { /* fall through to normal flow */ }
    }
  }, []);

  // Validate token with backend on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('token')) return; // handled above

    const storedToken = localStorage.getItem(STORAGE_TOKEN_KEY);

    if (!storedToken) {
      setIsLoading(false);
      return;
    }

    // Validate token against the API
    fetch('/api/auth/me', {
      headers: { Authorization: `Bearer ${storedToken}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error('Invalid token');
        return res.json();
      })
      .then((userData: AuthUser) => {
        setToken(storedToken);
        setUser(userData);
        localStorage.setItem(STORAGE_USER_KEY, JSON.stringify(userData));
      })
      .catch(() => {
        // Token invalid or expired — clear everything
        localStorage.removeItem(STORAGE_TOKEN_KEY);
        localStorage.removeItem(STORAGE_USER_KEY);
        setToken(null);
        setUser(null);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const login = useCallback((newToken: string, newUser: AuthUser) => {
    localStorage.setItem(STORAGE_TOKEN_KEY, newToken);
    localStorage.setItem(STORAGE_USER_KEY, JSON.stringify(newUser));
    setToken(newToken);
    setUser(newUser);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_TOKEN_KEY);
    localStorage.removeItem(STORAGE_USER_KEY);
    setToken(null);
    setUser(null);
  }, []);

  const updateUser = useCallback((updates: Partial<AuthUser>) => {
    setUser((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, ...updates };
      localStorage.setItem(STORAGE_USER_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  // Auto-logout after 30 minutes of inactivity (only when authenticated)
  useInactivityTimeout(
    useCallback(() => {
      if (token) {
        logout();
      }
    }, [token, logout]),
    INACTIVITY_TIMEOUT_MS
  );

  const isAuthenticated = !!token && !!user;

  return (
    <AuthContext.Provider
      value={{ user, token, isAuthenticated, isLoading, login, logout, updateUser }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextData {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
